import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getDriverCheckedList,
  approveDriverChecked,
  rejectDriverChecked,
} from 'service/DriverRegistrationService';
import DocChangeRequestsPanel from './DocChangeRequestsPanel';

const PAGE_SIZE = 20;

const STATUS_LABELS = { pending: 'На рассмотрении', approved: 'Одобрено', rejected: 'Отказано' };

const STATUS_COLORS = {
  pending:  { bg: '#fef9c3', color: '#92400e' },
  approved: { bg: '#dcfce7', color: '#15803d' },
  rejected: { bg: '#fee2e2', color: '#b91c1c' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const Pagination = ({ page, total, pageSize, onChange }) => {
  const totalPages = Math.ceil(total / pageSize) || 1;
  if (totalPages <= 1) return null;
  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) pages.push(i);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  const btn = (content, p, disabled = false) => (
    <button key={String(content) + p} onClick={() => !disabled && onChange(p)} disabled={disabled} style={{
      minWidth: 34, height: 34, padding: '0 8px', borderRadius: 7,
      border: p === page ? '1.5px solid #374151' : '1px solid #e5e7eb',
      background: p === page ? '#374151' : disabled ? '#f9fafb' : '#fff',
      color: p === page ? '#fff' : disabled ? '#d1d5db' : '#374151',
      cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: p === page ? 700 : 400,
    }}>{content}</button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 16 }}>
      {btn('‹', page - 1, page <= 1)}
      {pages.map((p, i) => p === '…' ? <span key={`e${i}`} style={{ padding: '0 4px', color: '#9ca3af' }}>…</span> : btn(p, p))}
      {btn('›', page + 1, page >= totalPages)}
      <span style={{ marginLeft: 8, fontSize: 12, color: '#9ca3af' }}>
        {((page - 1) * pageSize + 1)}–{Math.min(page * pageSize, total)} из {total}
      </span>
    </div>
  );
};

const highlight = (text, query) => {
  if (!text || !query) return text || '—';
  const idx = String(text).toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return String(text);
  const str = String(text);
  return (
    <>
      {str.slice(0, idx)}
      <mark style={{ background: '#fef08a', borderRadius: 2, padding: '0 1px' }}>{str.slice(idx, idx + query.length)}</mark>
      {str.slice(idx + query.length)}
    </>
  );
};

const DriverCheckedPanel = ({ setNotify }) => {
  const [records, setRecords]             = useState([]);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(false);
  const [statusFilter, setStatusFilter]   = useState('pending');
  const [page, setPage]                   = useState(1);
  const [expandedId, setExpandedId]       = useState(null);
  const [rejectModal, setRejectModal]     = useState(null);
  const [rejectReason, setRejectReason]   = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [previewUrl, setPreviewUrl]       = useState(null);
  const [searchInput, setSearchInput]     = useState('');
  const [searchQuery, setSearchQuery]     = useState('');
  const [statusCounts, setStatusCounts]   = useState({ pending: null, approved: null, rejected: null });
  const [searchFocused, setSearchFocused] = useState(false);
  const [viewMode, setViewMode]           = useState('registrations');
  const searchRef  = useRef(null);
  const debounceRef = useRef(null);

  const load = useCallback(async (p, q, sf) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (q) params.search = q;
      const res = await getDriverCheckedList(sf, params);
      const data = res.data;
      const items = data?.results || data || [];
      setRecords(items);
      const count = data?.count != null ? data.count : items.length;
      setTotal(count);
      setStatusCounts(prev => ({ ...prev, [sf]: count }));
    } catch (e) {
      setNotify({ isOpen: true, message: 'Ошибка загрузки заявок водителей', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [setNotify]);

  useEffect(() => { load(page, searchQuery, statusFilter); }, [page, searchQuery, statusFilter]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchQuery(val.trim());
    }, 350);
  };

  const handleStatusChange = (s) => {
    setStatusFilter(s);
    setPage(1);
    setExpandedId(null);
    setPreviewUrl(null);
  };

  // Load badge counts for all statuses on mount
  useEffect(() => {
    const loadAllCounts = async () => {
      try {
        const statuses = ['pending', 'approved', 'rejected'];
        const results = await Promise.all(statuses.map(s => getDriverCheckedList(s, { page: 1, page_size: 1 })));
        const counts = {};
        results.forEach((res, i) => {
          const d = res.data;
          counts[statuses[i]] = d?.count != null ? d.count : (d?.results || d || []).length;
        });
        setStatusCounts(counts);
      } catch (_) {}
    };
    loadAllCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await approveDriverChecked(id);
      setNotify({ isOpen: true, message: 'Водитель подтверждён и создан в системе', type: 'success' });
      await load(page, searchQuery, statusFilter);
      setStatusCounts(prev => ({
        ...prev,
        pending:  Math.max(0, (prev.pending  || 1) - 1),
        approved: (prev.approved || 0) + 1,
      }));
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setNotify({ isOpen: true, message: 'Укажите причину отказа', type: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      await rejectDriverChecked(rejectModal.id, rejectReason);
      setNotify({ isOpen: true, message: 'Заявка отклонена', type: 'success' });
      setRejectModal(null);
      setRejectReason('');
      await load(page, searchQuery, statusFilter);
      setStatusCounts(prev => ({
        ...prev,
        pending:  Math.max(0, (prev.pending  || 1) - 1),
        rejected: (prev.rejected || 0) + 1,
      }));
    } catch (e) {
      setNotify({ isOpen: true, message: e.response?.data?.detail || 'Ошибка', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const processedCount = (statusCounts.approved || 0) + (statusCounts.rejected || 0);

  if (viewMode === 'doc-changes') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1.5px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', width: 'fit-content' }}>
          <button onClick={() => setViewMode('registrations')} style={{ padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: '#fff', color: '#374151' }}>
            📋 Регистрации
          </button>
          <button onClick={() => setViewMode('doc-changes')} style={{ padding: '8px 20px', border: 'none', borderLeft: '1.5px solid #e5e7eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: '#374151', color: '#fff' }}>
            🔄 Смена документов
          </button>
        </div>
        <DocChangeRequestsPanel setNotify={setNotify} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1.5px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', width: 'fit-content' }}>
        <button onClick={() => setViewMode('registrations')} style={{ padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: '#374151', color: '#fff' }}>
          📋 Регистрации
        </button>
        <button onClick={() => setViewMode('doc-changes')} style={{ padding: '8px 20px', border: 'none', borderLeft: '1.5px solid #e5e7eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: '#fff', color: '#374151' }}>
          🔄 Смена документов
        </button>
      </div>
      {/* ── Stats bar ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: '8px 16px' }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <div style={{ fontSize: 10, color: '#16a34a', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Обработано</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#15803d', lineHeight: 1 }}>{processedCount}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg,#fefce8,#fef9c3)', border: '1px solid #fde047', borderRadius: 10, padding: '8px 16px' }}>
          <span style={{ fontSize: 20 }}>⏳</span>
          <div>
            <div style={{ fontSize: 10, color: '#a16207', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>На рассмотрении</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#92400e', lineHeight: 1 }}>{statusCounts.pending ?? '—'}</div>
          </div>
        </div>
      </div>

      {/* ── Search ────────────────────────────────────────── */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, pointerEvents: 'none', color: searchFocused ? '#374151' : '#9ca3af', transition: 'color 0.2s' }}>🔍</span>
        <input
          ref={searchRef}
          type="text"
          placeholder="Поиск по ФИО, гос. номеру, телефону, ИНН..."
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '11px 42px 11px 44px', borderRadius: 12, fontSize: 14,
            border: `1.5px solid ${searchFocused ? '#374151' : '#e5e7eb'}`,
            boxShadow: searchFocused ? '0 0 0 3px rgba(55,65,81,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
            background: searchFocused ? '#fff' : '#f9fafb', outline: 'none', transition: 'all 0.2s',
          }}
        />
        {searchInput && (
          <button onClick={() => { setSearchInput(''); setPage(1); setSearchQuery(''); searchRef.current?.focus(); }}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: '#e5e7eb', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', color: '#6b7280', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        )}
      </div>

      {/* ── Filter tabs ───────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => handleStatusChange(s)} style={{
            padding: '6px 16px', borderRadius: 6, cursor: 'pointer',
            border: statusFilter === s ? '1.5px solid #374151' : '1.5px solid #d1d5db',
            background: statusFilter === s ? '#374151' : '#fff',
            color: statusFilter === s ? '#fff' : '#374151',
            fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {STATUS_LABELS[s]}
            {statusCounts[s] != null && (
              <span style={{
                background: statusFilter === s ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                color: statusFilter === s ? '#fff' : '#374151',
                borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700,
              }}>{statusCounts[s]}</span>
            )}
          </button>
        ))}
        <button onClick={() => load(page, searchQuery, statusFilter)} style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 6, border: '1.5px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, color : 'black' }}>Обновить</button>
      </div>

      {/* ── Records ───────────────────────────────────────── */}
      {loading ? (
        <div style={{ padding: 40, color: '#6b7280', textAlign: 'center', fontSize: 14 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>Загрузка...
        </div>
      ) : records.length === 0 ? (
        <div style={{ padding: 40, color: '#6b7280', textAlign: 'center', fontSize: 14 }}>
          {searchQuery ? 'Ничего не найдено' : 'Нет записей'}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {records.map(dc => (
              <div key={dc.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {/* Summary row */}
                <div
                  onClick={() => { setExpandedId(expandedId === dc.id ? null : dc.id); setPreviewUrl(null); }}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto auto auto',
                    alignItems: 'center', gap: 12, padding: '11px 16px',
                    cursor: 'pointer', background: '#f9fafb',
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
                      {highlight(`${dc.last_name || ''} ${dc.first_name || ''} ${dc.middle_name || ''}`.trim(), searchQuery)}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                      {dc.vehicle_number && (
                        <span style={{ background: '#1e3a5f', color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em' }}>
                          {highlight(dc.vehicle_number, searchQuery)}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        ИНН: {dc.inn}&nbsp;|&nbsp;Тел: {highlight(dc.phone, searchQuery)}
                      </span>
                    </div>
                  </div>
                  {/* Date */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{formatDate(dc.created_at)}</span>
                  </div>
                  {/* Status */}
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                    background: STATUS_COLORS[dc.status]?.bg || '#f3f4f6',
                    color:      STATUS_COLORS[dc.status]?.color || '#374151',
                  }}>{STATUS_LABELS[dc.status]}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{expandedId === dc.id ? '▲' : '▼'}</span>
                </div>

                {/* Expanded details */}
                {expandedId === dc.id && (
                  <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '12px 20px', marginBottom: 16 }}>
                      {[
                        ['Гос. номер',        dc.vehicle_number || '—'],
                        ['Марка / модель',    dc.vehicle_model  || '—'],
                        ['Телефон',           dc.phone          || '—'],
                        ['ИНН',               dc.inn            || '—'],
                        ['Дата регистрации',  formatDate(dc.created_at)],
                      ].map(([label, val]) => (
                        <div key={label}>
                          <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</p>
                          <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 500 }}>{val}</p>
                        </div>
                      ))}
                    </div>

                    {/* Documents */}
                    <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 13 }}>Документы</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: previewUrl ? 12 : 16 }}>
                      {[
                        ['Фото ТС',        dc.photo_vehicle_url],
                        ['Паспорт (л)',    dc.passport_front_url],
                        ['Паспорт (о)',    dc.passport_back_url],
                        ['Права (л)',      dc.license_front_url],
                        ['Права (о)',      dc.license_back_url],
                        ['Тех.пасп. (л)', dc.tech_passport_front_url],
                        ['Тех.пасп. (о)', dc.tech_passport_back_url],
                      ].filter(([, url]) => url).map(([label, url]) => (
                        <button key={label} onClick={() => setPreviewUrl(previewUrl === url ? null : url)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '5px 10px', borderRadius: 6, fontSize: 12,
                            border: previewUrl === url ? '1.5px solid #374151' : '1px solid #d1d5db',
                            background: previewUrl === url ? '#f0f4ff' : '#fff',
                            color: '#374151', cursor: 'pointer', fontWeight: previewUrl === url ? 600 : 400,
                          }}
                        >📎 {label}</button>
                      ))}
                    </div>

                    {previewUrl && (
                      <div style={{ marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        {previewUrl.toLowerCase().includes('.pdf') ? (
                          <iframe src={previewUrl} title="preview" style={{ width: '100%', height: 480, border: 'none', display: 'block' }} />
                        ) : (
                          <img src={previewUrl} alt="preview" style={{ width: '100%', maxHeight: 480, objectFit: 'contain', display: 'block' }} />
                        )}
                      </div>
                    )}

                    {dc.rejection_reason && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                        <p style={{ margin: 0, fontSize: 13, color: '#b91c1c' }}><strong>Причина отказа:</strong> {dc.rejection_reason}</p>
                      </div>
                    )}

                    {dc.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={() => handleApprove(dc.id)} disabled={actionLoading}
                          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#15803d', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          ✓ Подтвердить
                        </button>
                        <button onClick={() => { setRejectModal({ id: dc.id, name: `${dc.last_name} ${dc.first_name}` }); setRejectReason(''); }}
                          disabled={actionLoading}
                          style={{ padding: '8px 20px', borderRadius: 8, border: '1.5px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          ✗ Отклонить
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={p => { setPage(p); setExpandedId(null); setPreviewUrl(null); }} />
        </>
      )}

      {/* ── Reject modal ──────────────────────────────────── */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Отклонить заявку</h3>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 13 }}>{rejectModal.name}</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Укажите причину отказа..." rows={4}
              style={{ width: '100%', borderRadius: 8, border: '1.5px solid #d1d5db', padding: '10px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectModal(null)} style={{ padding: '8px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Отмена</button>
              <button onClick={handleRejectSubmit} disabled={actionLoading}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {actionLoading ? 'Отправка...' : 'Отклонить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverCheckedPanel;
